'use client';

import { Card } from '@/components/ui/card';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Disc,
  Star,
  ArrowRight,
  Mail,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export function Footer() {
  return (
    <footer className="relative bg-gradient-to-b from-slate-900 to-slate-950 border-t border-white/[0.06] overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute bottom-0 left-0 w-full h-full opacity-5"
          style={{
            backgroundImage:
              'radial-gradient(circle at 0% 100%, rgba(59, 130, 246, 0.4), transparent 40%)',
          }}
        />
        <div
          className="absolute top-0 right-0 w-full h-full opacity-5"
          style={{
            backgroundImage:
              'radial-gradient(circle at 100% 0%, rgba(124, 58, 237, 0.4), transparent 40%)',
          }}
        />
      </div>

      <div className="container mx-auto py-10 md:py-16 px-4 relative z-10">
        {/* Logo & Description */}
        <div className="mb-10 md:mb-16 flex flex-col md:flex-row justify-between items-start gap-6 md:gap-10">
          <div className="mb-4 md:mb-0 max-w-md">
            <Link href="/" className="flex items-center group mb-3">
              <div className="relative">
                <motion.div
                  className="absolute inset-0 bg-blue-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100"
                  initial={{ scale: 0.8 }}
                  whileHover={{ scale: 1.2 }}
                  transition={{ duration: 0.3 }}
                />
                <Image
                  src="/watchinspect.svg"
                  alt="Watch Inspect Logo"
                  width={40}
                  height={40}
                  className="h-10 w-auto relative z-10 group-hover:scale-110 transition-transform duration-300"
                />
              </div>

              <div className="ml-3">
                <span className="text-xl font-bold relative">
                  <span className="tracking-tight text-white">
                    Watch Inspect
                  </span>
                  <motion.span
                    className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-400"
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                    transition={{ duration: 0.3 }}
                  />
                </span>
              </div>
            </Link>
            <p className="text-slate-400 mt-2 text-sm md:text-base">
              Your trusted source for watch analytics, market trends, and luxury
              timepiece data insights.
            </p>
            <div className="flex space-x-3 mt-4">
              <motion.a
                whileHover={{ scale: 1.1, y: -3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                href="https://twitter.com/watchinspect"
                target="_blank"
                rel="noopener"
                className="p-2 bg-slate-800/70 rounded-full hover:bg-blue-900/30 text-slate-400 hover:text-blue-400 transition-all duration-300"
              >
                <Twitter className="h-4 w-4" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.1, y: -3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                href="https://www.facebook.com/watchinspect"
                target="_blank"
                rel="noopener"
                className="p-2 bg-slate-800/70 rounded-full hover:bg-blue-900/30 text-slate-400 hover:text-blue-400 transition-all duration-300"
              >
                <Facebook className="h-4 w-4" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.1, y: -3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                href="https://www.instagram.com/watchinspect"
                target="_blank"
                rel="noopener"
                className="p-2 bg-slate-800/70 rounded-full hover:bg-blue-900/30 text-slate-400 hover:text-blue-400 transition-all duration-300"
              >
                <Instagram className="h-4 w-4" />
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.1, y: -3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                href="https://www.youtube.com/@WatchInspect"
                target="_blank"
                rel="noopener"
                className="p-2 bg-slate-800/70 rounded-full hover:bg-blue-900/30 text-slate-400 hover:text-blue-400 transition-all duration-300"
              >
                <Youtube className="h-4 w-4" />
              </motion.a>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="w-full md:w-auto">
            <div className="bg-slate-800/20 backdrop-blur-sm border border-slate-700/30 rounded-xl p-4 md:p-6 max-w-md">
              <h3 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-4 flex items-center">
                <Mail className="h-4 w-4 md:h-5 md:w-5 mr-2 text-blue-400" />
                Stay Updated
              </h3>
              <p className="text-slate-400 mb-3 text-xs md:text-sm">
                Subscribe for watch market insights.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <input
                  type="email"
                  placeholder="Enter email"
                  className="px-3 py-2 md:px-4 md:py-3 bg-slate-800/70 border border-slate-700/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-slate-300 w-full text-sm transition-all duration-300 hover:border-blue-500/30 focus:border-blue-500/50"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="px-3 py-2 md:px-4 md:py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-lg transition-all duration-300 flex items-center gap-2 w-full sm:w-auto justify-center whitespace-nowrap shadow-md hover:shadow-blue-500/20 text-sm"
                >
                  Subscribe <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Footer Links (Collapsible) */}
        <div className="block lg:hidden mb-8">
          <FooterAccordion />
        </div>

        {/* Desktop Footer Links (Regular Grid) */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-8">
          {/* Quick Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-800/80">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <FooterLink href="/dashboard">Dashboard</FooterLink>
              <FooterLink href="/marketplace">Marketplace</FooterLink>
              <FooterLink href="/charts">Price Charts</FooterLink>
              <FooterLink href="/blog">Blog</FooterLink>
              <FooterLink href="/forums">Forums</FooterLink>
            </ul>
          </div>

          {/* Support */}
          <div className="col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-800/80">
              Support
            </h3>
            <ul className="space-y-3">
              <FooterLink href="/help">Help Center</FooterLink>
              <FooterLink href="/contact">Contact Us</FooterLink>
              <FooterLink href="/faq">FAQ</FooterLink>
              <FooterLink href="/status">System Status</FooterLink>
            </ul>
          </div>

          {/* Legal */}
          <div className="col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-800/80">
              Legal
            </h3>
            <ul className="space-y-3">
              <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink href="/terms">Terms & Conditions</FooterLink>
              <FooterLink href="/do-not-sell">Do Not Sell My Info</FooterLink>
              <FooterLink href="/cookie-policy">Cookie Policy</FooterLink>
            </ul>
          </div>

          {/* Resources */}
          <div className="col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-slate-800/80">
              Resources
            </h3>
            <ul className="space-y-3">
              <FooterLink href="/api-docs">API Documentation</FooterLink>
              <FooterLink href="/security">Security</FooterLink>
              <FooterLink href="/developers">Developers</FooterLink>
              <FooterLink href="/press">Press Kit</FooterLink>
            </ul>
          </div>
        </div>

        {/* Trustpilot Section */}
        <div className="mt-8 md:mt-16">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 md:p-6 bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-xl">
            <div className="mb-3 sm:mb-0">
              <p className="text-slate-300 font-medium text-center sm:text-left text-sm md:text-base">
                Love using Watch Inspect? We&apos;d appreciate your review!
              </p>
            </div>
            <Link
              href="https://www.trustpilot.com/review/watchinspect.com"
              target="_blank"
              rel="noopener"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center px-3 py-2 md:px-5 md:py-3 rounded-lg bg-gradient-to-r from-green-600/80 to-green-700/80 text-white hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300"
              >
                <Star className="h-3 w-3 md:h-4 md:w-4 text-white mr-2" />
                <span className="text-xs md:text-sm font-medium">
                  Review on Trustpilot
                </span>
              </motion.div>
            </Link>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="mt-8 md:mt-16 pt-6 md:pt-8 border-t border-slate-800/50 text-center">
          <p className="text-slate-400 text-sm">
            © 2021 - {new Date().getFullYear()} WatchInspect DBA - Quinones
            Digital Asset Management LLC.
          </p>
          <p className="mt-2 md:mt-4 text-xs text-slate-500">
            All watch sales are derived in Miami, Florida solely by
            WatchInspect.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Footer Accordion for mobile view
function FooterAccordion() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    if (openSection === section) {
      setOpenSection(null);
    } else {
      setOpenSection(section);
    }
  };

  const sections = [
    {
      id: 'quickLinks',
      title: 'Quick Links',
      links: [
        { href: '/dashboard', label: 'Dashboard' },
        { href: '/marketplace', label: 'Marketplace' },
        { href: '/charts', label: 'Price Charts' },
        { href: '/blog', label: 'Blog' },
        { href: '/forums', label: 'Forums' },
      ],
    },
    {
      id: 'support',
      title: 'Support',
      links: [
        { href: '/help', label: 'Help Center' },
        { href: '/contact', label: 'Contact Us' },
        { href: '/faq', label: 'FAQ' },
        { href: '/status', label: 'System Status' },
      ],
    },
    {
      id: 'legal',
      title: 'Legal',
      links: [
        { href: '/privacy-policy', label: 'Privacy Policy' },
        { href: '/terms', label: 'Terms & Conditions' },
        { href: '/do-not-sell', label: 'Do Not Sell My Info' },
        { href: '/cookie-policy', label: 'Cookie Policy' },
      ],
    },
    {
      id: 'resources',
      title: 'Resources',
      links: [
        { href: '/api-docs', label: 'API Documentation' },
        { href: '/security', label: 'Security' },
        { href: '/developers', label: 'Developers' },
        { href: '/press', label: 'Press Kit' },
      ],
    },
  ];

  return (
    <div className="space-y-3">
      {sections.map((section) => (
        <div
          key={section.id}
          className="border border-slate-800/50 rounded-lg overflow-hidden"
        >
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full p-3 flex justify-between items-center bg-slate-800/20 hover:bg-slate-800/30 transition-colors"
          >
            <span className="font-semibold text-white text-sm">
              {section.title}
            </span>
            {openSection === section.id ? (
              <ChevronUp className="h-4 w-4 text-blue-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </button>

          {openSection === section.id && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-900/50"
            >
              <ul className="p-3 space-y-2">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-slate-400 hover:text-blue-400 transition-colors text-sm flex items-center"
                    >
                      <ArrowRight className="h-3 w-3 mr-2 text-blue-500/70" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
}

// Reusable footer link component with consistent styling
function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        href={href}
        className="text-slate-400 hover:text-blue-400 transition-colors duration-300 flex items-center group"
      >
        <motion.span
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
        >
          {children}
        </motion.span>
        <ArrowRight className="h-3 w-3 ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1" />
      </Link>
    </li>
  );
}

export default Footer;
